goog.declareModuleId('yext.m4.confcode.adminconsole.resources.ResourceNodeManager');

import {ResourceNode} from 'goog:yext.m4.confcode.adminconsole.shapes';

/**
 * Wraps the set of resources in the admin console and provides
 * an interface with which to safely modify/access them.
 *
 *
 * @author Ethan Cohen (ecohen@yext.com)
 */
export class ResourceNodeManager {
  /**
   * Instantiates the manager with the initial set of resources and a map from namespaced path to
   * file contents.
   *
   *
   * @param {Array<ResourceNode>} resources the resources
   * @param {Object} namespacedPathToContentMap a map of namespaced path to file contents.
   */
  constructor(resources, namespacedPathToContentMap) {
    const updatedResources = resources.map(r => {
      const resource = _.cloneDeep(r);
      resource['resourceTypeChildren'] = new Set(resource['resourceTypeChildren']);
      resource['namespacedChildren'] = new Set(resource['namespacedChildren']);
      return resource;
    });

    this.resourcesByNamespacedId_ =
      updatedResources.reduce((map, r) => {
        map.set(r['namespacedId'], r);
        return map;
      }, new Map());
    this.resourceByResourceTypeId_ =
      updatedResources.reduce((map, r) => {
        map.set(r['resourceTypeId'], r);
        return map;
      }, new Map());
    this.namespacedPathToContent_ = new Map(
      Object.entries(namespacedPathToContentMap || {}));
  }

  /**
   * Adds a file with the specified params.
   *
   * @param {string} namespace the namespace in which to add the file
   * @param {string} resourceType the resource type of which to add a file, e.g., km/entity
   * @param {string} fileName the file name to add
   */
  addFile(namespace, resourceType, fileName) {
    const resourceTypePath = resourceType + '/' + fileName;
    const resourceTypeParentId = resourceType;

    const namespacedPath = namespace + '/' + resourceTypePath;
    const namespacedParentId = namespace + '/' + resourceType;

    // if the namespace exists, the resource type directory does as
    // well -- this method will create if approriate
    if (!this.resourcesByNamespacedId_.has(namespacedParentId)) {
      this.addDirectory_(namespace, resourceType);
    }

    const node = {
      'resourceTypePath': resourceTypePath,
      'resourceTypeId': resourceTypePath,
      'resourceTypeParentId': resourceTypeParentId,
      'resourceTypeChildren': new Set(),
      'namespacedPath': namespacedPath,
      'namespacedId': namespacedPath,
      'namespacedParentId': namespacedParentId,
      'namespacedChildren': new Set(),
      'type': 'blob',
      'hasError': false,
      'expanded': false,
      'isBulkSelected': false,
      'resourceDisplayNames': {
        'default': fileName,
        'friendly': fileName,
      },
    };

    const resourceTypeParent =
      this.resourceByResourceTypeId_.get(resourceTypeParentId);
    const newResourceTypeParent = _.cloneDeep(resourceTypeParent);
    newResourceTypeParent['resourceTypeChildren'].add(resourceTypePath);
    newResourceTypeParent['namespacedChildren'].add(namespacedPath);
    this.resourceByResourceTypeId_.set(
      newResourceTypeParent['resourceTypeId'], newResourceTypeParent);
    this.resourcesByNamespacedId_.set(
      newResourceTypeParent['namespacedId'], newResourceTypeParent);

    const namespacedParent =
      this.resourcesByNamespacedId_.get(namespacedParentId);
    if (namespacedParent !== newResourceTypeParent) {
      const newNamespacedParent = _.cloneDeep(namespacedParent);
      newNamespacedParent['resourceTypeChildren'] = newResourceTypeParent['resourceTypeChildren'];
      newNamespacedParent['namespacedChildren'].add(namespacedPath);
      this.resourcesByNamespacedId_.set(newNamespacedParent['namespacedId'], newNamespacedParent);
      this.resourceByResourceTypeId_.set(
        newNamespacedParent['resourceTypeId'], newNamespacedParent);
    }

    this.resourceByResourceTypeId_.set(resourceTypePath, node);
    this.resourcesByNamespacedId_.set(namespacedPath, node);

    this.namespacedPathToContent_.set(namespacedPath, '');
  }

  /**
   * Adds a directory
   *
   * @private
   *
   * @param {string} namespace the namespace in which to add the directory
   * @param {string} resourceType the resource type of which to add the directory
   */
  addDirectory_(namespace, resourceType) {
    if (!this.resourcesByNamespacedId_.has(namespace)) {
      const namespaceNode = {
        'resourceTypePath': namespace,
        'resourceTypeId': namespace,
        'resourceTypeParentId': 'root',
        'resourceTypeChildren': new Set(),
        'namespacedPath': namespace,
        'namespacedId': namespace,
        'namespacedParentId': 'root',
        'namespacedChildren': new Set(),
        'type': 'tree',
        'hasError': false,
        'isBulkSelected': false,
        'expanded': false,
        'resourceDisplayNames': {
          'default': namespace,
          'friendly': namespace,
        },
      };
      this.resourcesByNamespacedId_.set(namespace, namespaceNode);
      this.resourceByResourceTypeId_.set(namespace, namespaceNode);

      const root = _.cloneDeep(this.resourcesByNamespacedId_.get('root'));
      root['namespacedChildren'].add(namespace);
      this.resourcesByNamespacedId_.set('root', root);

      const resourceTypeRoot = _.cloneDeep(this.resourceByResourceTypeId_.get('root'));
      resourceTypeRoot['namespacedChildren'].add(namespace);
      this.resourceByResourceTypeId_.set('root', resourceTypeRoot);
    }

    const directories = resourceType.split('/');
    let resourceTypeParentId = 'root';
    let currentResourceTypePath = directories[0];

    directories.forEach(directory => {
      if (directory != currentResourceTypePath) {
        currentResourceTypePath = currentResourceTypePath + '/' + directory;
      }
      const currentNamespacedPath = namespace + '/' + currentResourceTypePath;
      const namespacedParentId = resourceTypeParentId === 'root'
        ? namespace
        : namespace + '/' + resourceTypeParentId;
      const node = {
        'resourceTypePath': currentResourceTypePath,
        'resourceTypeId': currentResourceTypePath,
        'resourceTypeParentId': resourceTypeParentId,
        'resourceTypeChildren': new Set(),
        'namespacedPath': currentNamespacedPath,
        'namespacedId': currentNamespacedPath,
        'namespacedParentId': namespacedParentId,
        'namespacedChildren': new Set(),
        'type': 'tree',
        'hasError': false,
        'isBulkSelected': false,
        'expanded': false,
        'resourceDisplayNames': {
          'default': directory,
          'friendly': directory,
        },
      };

      const resourceTypeParentNode =
        this.getResourceByResourceTypeId(resourceTypeParentId);

      if (!resourceTypeParentNode['resourceTypeChildren'].has(currentResourceTypePath)) {
        resourceTypeParentNode['resourceTypeChildren'].add(currentResourceTypePath);

        this.resourceByResourceTypeId_.set(
          resourceTypeParentId,
          resourceTypeParentNode);
        this.resourcesByNamespacedId_.set(
          resourceTypeParentNode['namespacedId'],
          resourceTypeParentNode);
      }

      const namespacedParent =
        this.getResourceByNamespaceId(namespacedParentId);
      if (!namespacedParent['namespacedChildren'].has(currentNamespacedPath)) {
        namespacedParent['namespacedChildren'].add(currentNamespacedPath);

        // when adding a resource type in a namespace for which we dont have that group yet.
        // need to update the resource type children
        // For example, if we have default/km/entity and then add ce/km/entity-type, both 'ce/km'
        // and 'default/km' should have both 'entity' and 'entity-type' as resource type children.
        const resourceTypeChildren = new Set();
        namespacedParent['resourceTypeChildren'].forEach(id => resourceTypeChildren.add(id));
        resourceTypeParentNode['resourceTypeChildren'].forEach(id => resourceTypeChildren.add(id));
        resourceTypeChildren.add(currentResourceTypePath);
        namespacedParent['resourceTypeChildren'] = resourceTypeChildren;

        this.resourcesByNamespacedId_.set(namespacedParentId, namespacedParent);
        this.resourceByResourceTypeId_.set(
          namespacedParent['resourceTypeId'],
          namespacedParent);
      }

      if (!this.resourceByResourceTypeId_.has(currentResourceTypePath)) {
        this.resourceByResourceTypeId_.set(currentResourceTypePath, node);
      }

      if (!this.resourcesByNamespacedId_.has(currentNamespacedPath)) {
        this.resourcesByNamespacedId_.set(currentNamespacedPath, node);
      }
      resourceTypeParentId = currentResourceTypePath;
    });
  }

  /**
   * Deletes a file as specified by its namespaced id.
   *
   * @param {string} namespacedId the namespaced id
   */
  deleteFileByNamespacedId(namespacedId) {
    if (!this.resourcesByNamespacedId_.has(namespacedId)) {
      return;
    }

    const node = this.resourcesByNamespacedId_.get(namespacedId);
    const resourceTypeParent =
      this.resourceByResourceTypeId_.get(node['resourceTypeParentId']);
    if (resourceTypeParent) {
      const childrenOfParent = resourceTypeParent['resourceTypeChildren'];
      childrenOfParent.delete(node['resourceTypeId']);
    }

    if (this.resourcesByNamespacedId_.has(node['namespacedParentId'])) {
      const namespacedParent =
        this.resourcesByNamespacedId_.get(node['namespacedParentId']);
      const childrenOfParent = namespacedParent['namespacedChildren'];
      childrenOfParent.delete(node['namespacedId']);
    }

    this.resourcesByNamespacedId_.delete(namespacedId);
    this.resourceByResourceTypeId_.delete(node['resourceTypeId']);
    this.namespacedPathToContent_.delete(node['namespacedPath']);
  }

  /**
   * Deletes a file as specified by its resource type id.
   *
   * @param {string} resourceTypeId the resource type id
   */
  deleteFileByResourceTypeId(resourceTypeId) {
    if (this.resourceByResourceTypeId_.has(resourceTypeId)) {
      const node = this.resourceByResourceTypeId_.get(resourceTypeId);
      this.deleteFileByNamespacedId(node['namespacedId']);
    }
  }

  /**
   * Deletes a namespaced directory.
   *
   * Does not delete the corresponding resource type directory as it may still have children.
   *
   * @param {string} namespacedId the namespaced id
   */
  deleteNamespacedDirectory(namespacedId) {
    const node = this.resourcesByNamespacedId_.get(namespacedId);
    if (!node) {
      return;
    }

    const children = node['namespacedChildren'];

    const parent = this.resourcesByNamespacedId_.get(node['namespacedParentId']);
    if (parent) {
      const parentChildren = parent['namespacedChildren'];
      parentChildren.delete(namespacedId);

      this.resourceByResourceTypeId_.set(parent['resourceTypeId'], parent);
      this.resourcesByNamespacedId_.set(parent['namespacedId'], parent);
    }

    this.resourcesByNamespacedId_.delete(namespacedId);

    children.forEach(childId => {
      const childNode = this.resourcesByNamespacedId_.get(childId);
      if (childNode.type === 'tree') {
        this.deleteNamespacedDirectory(childId);
      } else {
        this.deleteFileByNamespacedId(childId);
      }
    });
  }

  /**
   * Deletes the directory specified by the resource type id.
   *
   * @param {string} resourceTypeId the resource type id
   */
  deleteResourceTypeDirectory(resourceTypeId) {
    const node = this.resourceByResourceTypeId_.get(resourceTypeId);
    if (!node) {
      return;
    }

    const children = node['resourceTypeChildren'];

    const parent = this.resourceByResourceTypeId_.get(node['resourceTypeParentId']);
    if (parent) {
      const parentChildren = parent['resourceTypeChildren'];
      parentChildren.delete(resourceTypeId);

      this.resourceByResourceTypeId_.set(parent['resourceTypeId'], parent);
      this.resourcesByNamespacedId_.set(parent['namespacedId'], parent);
    }

    this.resourceByResourceTypeId_.delete(resourceTypeId);

    children.forEach(childId => {
      const childNode = this.resourceByResourceTypeId_.get(childId);
      if (childNode.type === 'tree') {
        this.deleteResourceTypeDirectory(childId);
      } else {
        this.deleteFileByResourceTypeId(childId);
      }
    });
  }

  /**
   * Gets the set of bulk selected files/directories.
   *
   * @param {boolean} organizeByResourceTypes true if children/parents should be determined by resource types
   * @return {{
   * directoriesSelected: Array<string>,
   * filesSelected:Array<string>}}
   */
  getBulkSelected(organizeByResourceTypes) {
    let bulkSelected = {
      directoriesSelected: [],
      filesSelected: [],
    };
    if (organizeByResourceTypes) {
      Array.from(this.resourceByResourceTypeId_.values())
        .filter(n => n['isBulkSelected'])
        .forEach(n => {
          if (n['resourceTypeChildren'].size > 1) {
            bulkSelected.directoriesSelected.push(n['resourceTypePath']);
          } else {
            bulkSelected.filesSelected.push(n['resourceTypePath']);
          }
        });
    } else {
      Array.from(this.resourcesByNamespacedId_.values())
        .filter(n => n['isBulkSelected'])
        .forEach(n => {
          if (n['namespacedChildren'].size > 1) {
            bulkSelected.directoriesSelected.push(n['namespacedPath']);
          } else {
            bulkSelected.filesSelected.push(n['namespacedPath']);
          }
        });
    }

    return bulkSelected;
  }

  /**
   * Deletes the specified files/directories.
   *
   *
   * @param {boolean} organizeByResourceTypes whether the provided ids are resource type organized or namespaced
   * @param {{
   * directoriesSelected: Array<string>,
   * filesSelected:Array<string>}} bulkSelected the selected files/directories
   */
  deleteBulkSelected(organizeByResourceTypes, bulkSelected) {
    const ancestorDirectories = this._findCommonAncestors(bulkSelected.directoriesSelected);
    if (organizeByResourceTypes) {
      bulkSelected.filesSelected.forEach(id => this.deleteFileByResourceTypeId(id));
      ancestorDirectories.forEach(id => this.deleteResourceTypeDirectory(id));
    } else {
      bulkSelected.filesSelected.forEach(id => this.deleteFileByNamespacedId(id));
      ancestorDirectories.forEach(id => this.deleteNamespacedDirectory(id));
    }
  }

  /**
   * Converts the provided directories into their minimal common ancestors.
   * For example ["", "c", "c/km", "c/km/entity", "ce/km", "ce/km/entity"] into ["c", "ce/km"].
   *
   * @param {Array<string>} directories
   * @returns {Array<string>} common ancestors
   */
  _findCommonAncestors(directories) {
    directories = directories.sort((a, b) => a.length - b.length);
    let results = [];
    directories.forEach(directory => {
      if (!directory) {
        return;
      }
      let noAncestor = !results.some(result => {
        return directory.startsWith(result + '/');
      });
      if (noAncestor) {
        results.push(directory);
      }
    });
    return results;
  }

  /**
   *
   * @returns a map of resources by their resource type path
   */
  getResourcesByResourceTypePath() {
    return _.cloneDeep(this.resourceByResourceTypeId_);
  }

  /**
   *
   * @returns a map of reosurces by their namespaced path
   */
  getResourcesByNamespacePath() {
    return _.cloneDeep(this.resourcesByNamespacedId_);
  }

  /**
   * Gets the resource with the specified resource type id.
   *
   * @param {string} resourceTypeId the resource type id
   * @returns the resource
   */
  getResourceByResourceTypeId(resourceTypeId) {
    return _.cloneDeep(this.resourceByResourceTypeId_.get(resourceTypeId));
  }

  /**
   * Gets the resource with the specified namespced id.
   *
   * @param {string} namespacedId the namespaced id
   * @returns the resource
   */
  getResourceByNamespaceId(namespacedId) {
    return _.cloneDeep(this.resourcesByNamespacedId_.get(namespacedId));
  }

  /**
   * Gets the resource with the specified id.
   *
   * @param {string} id the resource id
   * @param {boolean} organizeByResourceTypes if the id is a resource type id
   * @returns the resource
   */
  getResourceById(id, organizeByResourceTypes) {
    return organizeByResourceTypes
      ? this.getResourceByResourceTypeId(id)
      : this.getResourceByNamespaceId(id);
  }

  /**
   * Updates the resource with the provided object.
   *
   * @param {ResourceNode} resource the resource
   */
  updateResource(resource) {
    this.resourceByResourceTypeId_.set(resource['resourceTypeId'], resource);
    this.resourcesByNamespacedId_.set(resource['namespacedId'], resource);
  }

  /**
   * Updates the resource content for the resource with the specified namespace path.
   *
   *
   * @param {string} namespacedPath the name spaced path
   * @param {string} content the resource content
   */
  updateResourceContentForNamespacedPath(namespacedPath, content) {
    if (this.namespacedPathToContent_.has(namespacedPath)) {
      this.namespacedPathToContent_.set(namespacedPath, content);
    }
  }

  /**
   * Updates the resource content for the specified resource.
   *
   *
   * @param {boolean} organizeByResourceTypes true if the path is a resource type path
   * @param {string} path the path
   * @param {string} content the content
   */
  updateResourceContent(organizeByResourceTypes, path, content) {
    if (organizeByResourceTypes) {
      const resource = this.resourceByResourceTypeId_.get(path);
      if (!resource) {
        return;
      }

      this.updateResourceContentForNamespacedPath(
        resource['namespacedPath'],
        content,
      );
    } else {
      this.updateResourceContentForNamespacedPath(path, content);
    }
  }

  /**
   * Gets the resource content for the specified namespace path.
   *
   * @param {string} namespacedPath the namespace path
   * @return {string} the content
   */
  getResourceContentForNamespacePath(namespacedPath) {
    return _.cloneDeep(this.namespacedPathToContent_.get(namespacedPath));
  }

  /**
   * Gets the resource content for the specified resource type path.
   *
   * @param {string} resourceTypePath the resource type path
   * @return {string} the content
   */
  getResourceContentForResourceTypePath(resourceTypePath) {
    const node = this.resourceByResourceTypeId_.get(resourceTypePath);
    if (node) {
      return _.cloneDeep(this.getResourceContentForNamespacePath(node['namespacedPath']));
    }
    return '';
  }

  /**
   * Gets the resource content.
   *
   * @param {boolean} organizeByResourceTypes true if the path is a resource type path
   * @param {string} path the path
   * @return {string} the content
   */
  getResourceContent(organizeByResourceTypes, path) {
    if (organizeByResourceTypes) {
      return this.getResourceContentForResourceTypePath(path);
    }
    return this.getResourceContentForNamespacePath(path);
  }

  /**
   * @return {!Map<string, string>} map from namespaced path to the resource content
   */
  getNamespacedPathToContentMap() {
    return _.cloneDeep(this.namespacedPathToContent_);
  }

  /**
   * Collapses the node and its children.
   *
   * @param {boolean} organizeByResourceType true if the children should be calculated by resource type
   * @param {string} nodeId  the node id
   */
  collapseNodeAndChildren(organizeByResourceType, nodeId) {
    if (organizeByResourceType) {
      this.collapseNodeAndChildrenForResourceType(nodeId);
    } else {
      this.collapseNodeAndChildrenForNamespace(nodeId);
    }
  }

  /**
   * Collapses the node and its children.
   *
   * @param {string} nodeId the node id in resource type format
   */
  collapseNodeAndChildrenForResourceType(nodeId) {
    const node = this.resourceByResourceTypeId_.get(nodeId);
    node['expanded'] = false;

    node['resourceTypeChildren'].forEach(id => this.collapseNodeAndChildrenForResourceType(id));
  }

  /**
   * Collapses the node and its children.
   *
   * @param {string} nodeId  the namespaced node id
   */
  collapseNodeAndChildrenForNamespace(nodeId) {
    const node = this.resourcesByNamespacedId_.get(nodeId);
    node['expanded'] = false;

    node['namespacedChildren'].forEach(id => this.collapseNodeAndChildrenForNamespace(id));
  }

  /**
   * Expands the node and its parents.
   *
   * @param {boolean} organizeByResourceTypes true if the node id is in resource type format
   * @param {string} nodeId the node id
   */
  expandNodeAndParents(organizeByResourceTypes, nodeId) {
    if (organizeByResourceTypes) {
      this.expandNodeAndParentsForResourceType(nodeId);
    } else {
      this.expandNodeAndParentsForNamespace(nodeId);
    }
  }

  /**
   * Expands the node and its parents.
   *
   * @param {string} nodeId the namespaced node id
   */
  expandNodeAndParentsForNamespace(nodeId) {
    this.actOnNodeAndParentsForNamespacedPath_(nodeId, 'expanded', true);
  }

  /**
   * Expands the node and its parents.
   *
   * @param {string} nodeId the resource type node id
   */
  expandNodeAndParentsForResourceType(nodeId) {
    const node = this.resourceByResourceTypeId_.get(nodeId);
    if (!node) {
      return;
    }

    node['expanded'] = true;
    this.resourceByResourceTypeId_.set(nodeId, node);
    const parentId = node['resourceTypeParentId'];
    if (!parentId || !this.resourceByResourceTypeId_.has(parentId)) {
      return;
    }
    this.expandNodeAndParentsForResourceType(parentId);
  }


  /**
   * Sets errors for the specified node
   *
   * @param {string} namespacedNodePath  the namespaced node path
   */
  setErrorsForNode(namespacedNodePath) {
    this.actOnNodeAndParentsForNamespacedPath_(
      namespacedNodePath,
      'hasError',
      true,
    );
  }

  /**
   * Clears errors for the specified node.
   *
   * @param {string} namespacedNodePath the namespaced node path
   */
  clearErrorsForNode(namespacedNodePath) {
    this.actOnNodeAndParentsForNamespacedPath_(
      namespacedNodePath,
      'hasError',
      false,
    );
  }

  /**
   * Sets the desired value for the specified property on the given node and
   * its parents.
   *
   *  @private
   *
   * @param {string} namespacedPath the namespaced path
   * @param {string} prop the property
   * @param {*} val the value
   */
  actOnNodeAndParentsForNamespacedPath_(namespacedPath, prop, val) {
    const node = this.resourcesByNamespacedId_.get(namespacedPath);
    if (!node) {
      return;
    }


    node[prop] = val;
    this.resourcesByNamespacedId_.set(namespacedPath, node);
    this.resourceByResourceTypeId_.set(node['resourceTypeId'], node);
    const parentId = node['namespacedParentId'];
    if (!parentId || !this.resourcesByNamespacedId_.has(parentId)) {
      return;
    }
    this.actOnNodeAndParentsForNamespacedPath_(parentId, prop, val);
  }

  /**
   * Sets the desired value for the specified property on the given node and
   * its parents.
   * @private
   *
   * @param {string} resourceTypePath
   * @param {string} prop
   * @param {*} val
   */
  actOnNodeAndParentsForResourceTypePath_(resourceTypePath, prop, val) {
    const node = this.resourceByResourceTypeId_.get(resourceTypePath);
    if (!node) {
      return;
    }

    node[prop] = val;
    this.resourceByResourceTypeId_.set(resourceTypePath, node);
    this.resourcesByNamespacedId_.set(node['namespacedId'], node);

    const parentId = node['resourceTypeParentId'];
    if (!parentId || !this.resourceByResourceTypeId_.has(parentId)) {
      return;
    }
    this.actOnNodeAndParentsForResourceTypePath_(parentId, prop, val);
  }

  /**
   * Update the bulk selected flag for the specified node.
   *
   * @param {boolean} organizeByResourceType whether or not the node id is of resource type format
   * @param {string} nodeId the node id
   * @param {boolean} shouldBeBulk the desired flag value
   */
  updateBulkSelected(organizeByResourceType, nodeId, shouldBeBulk) {
    if (organizeByResourceType) {
      this.updateBulkSelectedForResourceType(nodeId, shouldBeBulk);
    } else {
      this.updateBulkSelectedForNamespaced(nodeId, shouldBeBulk);
    }
  }

  /**
   * Update the bulk selected flag for the resource type node.
   *
   * @param {string} nodeId the node id
   * @param {boolean} shouldBeBulk the desired flag value
   */
  updateBulkSelectedForResourceType(nodeId, shouldBeBulk) {
    const node = this.resourceByResourceTypeId_.get(nodeId);
    node['isBulkSelected'] = shouldBeBulk;
    this.resourceByResourceTypeId_.set(nodeId, node);
    node['resourceTypeChildren'].forEach(childId => this.updateBulkSelectedForResourceType(childId, shouldBeBulk));

    if (!shouldBeBulk) {
      this.actOnNodeAndParentsForResourceTypePath_(
        nodeId, 'isBulkSelected', false);
    }
  }


  /**
   * Updated the bulk selected flag for the namespaced node.
   *
   * @param {string} nodeId the node id
   * @param {boolean} shouldBeBulk the desired flag value
   */
  updateBulkSelectedForNamespaced(nodeId, shouldBeBulk) {
    const node = this.resourcesByNamespacedId_.get(nodeId);
    node['isBulkSelected'] = shouldBeBulk;
    this.resourcesByNamespacedId_.set(nodeId, node);
    node['namespacedChildren'].forEach(childId => this.updateBulkSelectedForNamespaced(childId, shouldBeBulk));

    if (!shouldBeBulk) {
      this.actOnNodeAndParentsForNamespacedPath_(
        nodeId, 'isBulkSelected', false);
    }
  }
}
